import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || import.meta.env.SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase credentials not found. Database features will be disabled.');
}

export const supabase = createClient(
  supabaseUrl || '',
  supabaseAnonKey || ''
);

// ============================================================================
// TYPES - Based on our schema
// ============================================================================

export interface SupplierPriority {
  id?: number;
  supplier_name: string;
  priority: number;
  created_at?: string;
  updated_at?: string;
}

export interface Product {
  id?: number;
  sku: string;
  name?: string;
  slug?: string;
  global_unique_id?: string;

  status?: string;
  type?: string;
  featured?: boolean;
  catalog_visibility?: string;

  regular_price?: number;
  sale_price?: number;
  date_on_sale_from?: string;
  date_on_sale_to?: string;

  manage_stock?: boolean;
  stock_quantity?: number;
  stock_status?: string;
  backorders?: string;

  weight?: string;
  length?: string;
  width?: string;
  height?: string;
  shipping_class?: string;
  virtual?: boolean;

  tax_status?: string;
  tax_class?: string;

  description?: string;
  short_description?: string;

  images?: any[];
  categories?: any[];
  tags?: any[];
  attributes?: any[];
  default_attributes?: any[];
  meta_data?: Record<string, any>;

  // Direct columns (not in meta_data)
  nabavna_cijena?: number;
  pdv?: string;
  garancija?: string;
  dobavljac?: string;
  specifikacije?: Record<string, any>;
  ean?: string;
  brand?: string;

  upsell_ids?: number[];
  cross_sell_ids?: number[];
  grouped_products?: number[];
  parent_id?: number;

  downloadable?: boolean;
  downloads?: any[];
  download_limit?: number;
  download_expiry?: number;

  external_url?: string;
  button_text?: string;

  reviews_allowed?: boolean;
  menu_order?: number;
  purchase_note?: string;
  sold_individually?: boolean;

  feed_source?: string;
  source_hash?: string;
  raw_feed_data?: any;
  last_feed_update?: string;

  // Multi-supplier fields
  supplier_stocks?: Record<string, number>;  // { "ASBIS": 10, "AVTERA": 5 }
  supplier_prices?: Record<string, number>;  // { "ASBIS": 125.50, "AVTERA": 120.00 }
  available_suppliers?: string[];  // ["ASBIS", "AVTERA"]

  created_at?: string;
  updated_at?: string;
  synced_to_woo_at?: string;
}

export interface CustomFieldDefinition {
  id?: number;
  key: string;
  label: string;
  field_type: 'text' | 'number' | 'price' | 'boolean' | 'select' | 'date';
  options?: string[];
  required?: boolean;
  default_value?: string;
  min_value?: number;
  max_value?: number;
  visible_in_list?: boolean;
  visible_in_detail?: boolean;
  sortable?: boolean;
  filterable?: boolean;
  sync_to_woo?: boolean;
  woo_meta_key?: string;
  position?: number;
  created_at?: string;
}

export interface Supplier {
  id?: number;
  name: string;
  code?: string;
  feed_url?: string;
  feed_type?: string;
  feed_username?: string;
  feed_password?: string;
  field_mappings?: Record<string, string>;
  default_markup?: number;
  priority?: number;
  enabled?: boolean;
  last_import_at?: string;
  created_at?: string;
}

export interface ProductChange {
  id?: number;
  product_id?: number;
  sku: string;
  change_type: string;
  changed_fields?: string[];
  old_values?: Record<string, any>;
  new_values?: Record<string, any>;
  synced?: boolean;
  synced_at?: string;
  sync_error?: string;
  created_at?: string;
}

// ============================================================================
// PRODUCT OPERATIONS
// ============================================================================

// productService now proxies every call through the Firebase-authenticated
// admin API (service_role server-side). Kept as a drop-in replacement so
// existing pages (DbProducts, FeedManager) continue working unchanged.
export const productService = {
  // Get all products with pagination
  async getAll(page = 1, limit = 50) {
    const offset = (page - 1) * limit;
    const api = await _adminApi();
    const result = await api.products.list(
      { offset, limit, order_by: 'updated_at', order_dir: 'desc' },
      true
    );
    // list(withCount=true) returns { rows, total }
    const r = result as { rows: any[]; total: number };
    return { data: r.rows, count: r.total };
  },

  // Get single product by SKU
  async getBySku(sku: string) {
    try {
      return await (await _adminApi()).products.getBySku(sku);
    } catch (err: any) {
      if (String(err?.message || '').includes('404')) return null;
      throw err;
    }
  },

  // Upsert product (insert or update based on SKU)
  async upsert(product: Product) {
    const data = await (await _adminApi()).products.upsert([product]);
    return data?.[0];
  },

  // Bulk upsert (simple - overwrites everything)
  async bulkUpsert(products: Product[]) {
    return (await _adminApi()).products.upsert(products);
  },

  // Smart bulk upsert with supplier priority and stock aggregation.
  // All logic is executed server-side under service_role, so the client
  // never needs direct Supabase access to products / supplier_priority.
  async smartBulkUpsert(
    products: Product[],
    supplierName: string,
    updateFields?: Record<string, boolean>
  ) {
    const api = await _adminApi();
    const result = await api.products.smartBulkUpsert({
      supplierName,
      products,
      updateFields,
    });
    return result;
  },


  // Search products (server-side filtering via admin API). The meta_data.contains
  // filter for "supplier" is omitted here — use the dedicated dobavljac column.
  async search(query: string, filters?: {
    status?: string;
    stock_status?: string;
    feed_source?: string;
    supplier?: string;
  }) {
    const api = await _adminApi();
    return api.products.list({
      search: query,
      status: filters?.status ? [filters.status] : undefined,
      stock_status: filters?.stock_status ? [filters.stock_status] : undefined,
      dobavljac: filters?.supplier ? [filters.supplier] : undefined,
      limit: 100,
    });
  },

  // Get products count by status (with pagination to handle >1000 products)
  // Status/stock/feed breakdown via paginated list through admin API.
  async getStats() {
    const api = await _adminApi();
    const PAGE_SIZE = 1000;
    let offset = 0;
    const stats = {
      total: 0,
      byStatus: {} as Record<string, number>,
      byStockStatus: {} as Record<string, number>,
      byFeedSource: {} as Record<string, number>,
    };
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const rows = (await api.products.list({
        fields: ['status', 'stock_status', 'feed_source'],
        offset,
        limit: PAGE_SIZE,
      })) as any[];
      if (!rows || rows.length === 0) break;
      for (const p of rows) {
        stats.total += 1;
        const s = p.status || 'unknown';
        const ss = p.stock_status || 'unknown';
        const fs = p.feed_source || 'unknown';
        stats.byStatus[s] = (stats.byStatus[s] || 0) + 1;
        stats.byStockStatus[ss] = (stats.byStockStatus[ss] || 0) + 1;
        stats.byFeedSource[fs] = (stats.byFeedSource[fs] || 0) + 1;
      }
      if (rows.length < PAGE_SIZE) break;
      offset += PAGE_SIZE;
      if (offset > 1_000_000) break;
    }
    return stats;
  },
};

// ============================================================================
// CUSTOM FIELD DEFINITIONS
// ============================================================================

// Routes through the Firebase-authenticated admin API.
export const customFieldService = {
  async getAll() {
    return (await _adminApi()).customFields.list();
  },
  async create(field: CustomFieldDefinition) {
    return (await _adminApi()).customFields.create(field);
  },
  async update(id: number, field: Partial<CustomFieldDefinition>) {
    return (await _adminApi()).customFields.update(id, field);
  },
  async delete(id: number) {
    await (await _adminApi()).customFields.remove(id);
  },
};

// ============================================================================
// SUPPLIERS
// ============================================================================

// Routes through the Firebase-authenticated admin API on the server.
// Lazy import to avoid circular dep with ./adminApi (which imports firebase config).
export const supplierService = {
  async getAll() {
    return (await _adminApi()).suppliers.list();
  },

  async create(supplier: Supplier) {
    return (await _adminApi()).suppliers.create(supplier);
  },

  async update(id: number, supplier: Partial<Supplier>) {
    return (await _adminApi()).suppliers.update(id, supplier);
  },
};

// ============================================================================
// SUPPLIER PRIORITY
// ============================================================================

// Routes through the Firebase-authenticated admin API.
export const supplierPriorityService = {
  async getAll(): Promise<SupplierPriority[]> {
    const data = await (await _adminApi()).supplierPriority.list();
    return (data || []) as SupplierPriority[];
  },

  async updatePriority(supplierName: string, priority: number) {
    const rows = await (await _adminApi()).supplierPriority.upsert({
      supplier_name: supplierName,
      priority,
    });
    return rows?.[0];
  },

  async reorder(suppliers: { supplier_name: string; priority: number }[]) {
    // Single batch upsert instead of N parallel requests.
    await (await _adminApi()).supplierPriority.upsert(suppliers);
  },

  async delete(supplierName: string) {
    await (await _adminApi()).supplierPriority.remove(supplierName);
  },
};

// ============================================================================
// CHANGE LOG
// ============================================================================

// Routes through the Firebase-authenticated admin API.
export const changeLogService = {
  async getPending(limit = 100) {
    return (await _adminApi()).productChanges.list({ pending: true, limit });
  },
  async markSynced(ids: number[]) {
    await (await _adminApi()).productChanges.markSynced(ids);
  },
  async getRecent(limit = 50) {
    return (await _adminApi()).productChanges.list({ limit });
  },
};

// ============================================================================
// WOO SHOPS - WooCommerce target shops configuration
// ============================================================================

// Per-supplier filter configuration
export interface SupplierFilter {
  supplier: string;
  enabled: boolean;
  min_stock_quantity: number;
  min_price: number;
  stock_status: string[];  // 'instock', 'outofstock', 'onbackorder'
  categories: string[];    // opciono: samo određene kategorije za tog dobavljača
  // Brendovi dozvoljeni unutar pojedine kategorije: { "PC Tablet": ["SAMSUNG"] }.
  // Kategorija koje nema u mapi, ili ima praznu listu = svi brendovi.
  category_brands?: Record<string, string[]>;
  excluded_categories?: string[]; // Isključene source kategorije (blacklist) — exclude pobjeđuje allow listu
  image_proxy_url?: string; // Proxy URL za slike, npr. "https://proxy.com/img?url={url}"
}

// Izuzetak sinhronizacije po kategoriji: polja koja se NE ažuriraju na
// postojećim proizvodima čija efektivna source kategorija matchuje listu.
// Kreacija novih proizvoda nije pogođena. Vidi getCategoryExcludedFields
// u api/lib/syncProductLogic.ts.
export interface CategoryFieldExclusion {
  enabled?: boolean;    // default true
  categories: string[]; // source kategorije (case-insensitive match)
  fields: string[];     // sync polja: regular_price, sale_price, stock_quantity, ...
}

export interface WooShopSourceFilters {
  // Globalni filteri (primjenjuju se na sve dobavljače koji nemaju specifičan filter)
  product_status: string[];
  stock_status: string[];
  sku_pattern: string | null;
  min_stock_quantity: number;
  suppliers: string[];  // lista odabranih dobavljača (za kompatibilnost)

  // PER-SUPPLIER FILTERI - napredna opcija
  supplier_filters?: SupplierFilter[];

  // IZUZECI SINHRONIZACIJE PO KATEGORIJI
  category_field_exclusions?: CategoryFieldExclusion[];

  custom_meta_filters: {
    meta_key: string;
    operator: string;
    value: string;
  }[];
  source_product_id_field: string;
}

export interface CustomFieldMapping {
  sourceField: string;
  targetField: string;
  applyTo: 'all' | 'new' | 'existing';
  enabled: boolean;
}

export interface WooShop {
  /** Owner approval for the separately billed review email module. */
  enable_review_emails?: boolean;
  /** Owner approval for the separately billed OLX AI replies module. */
  enable_olx_ai_replies?: boolean;
  id?: string;
  name: string;
  description?: string;
  url: string;
  consumer_key: string;
  consumer_secret: string;
  is_active: boolean;
  sync_fields: Record<string, boolean>;
  field_mappings: Record<string, string>;
  price_adjustment: {
    type: 'none' | 'percentage' | 'fixed';
    value: number;
    round_to: number | null;
  };
  category_mappings: Record<string, number>;
  sync_options: {
    create_new_products: boolean;
    update_existing_products: boolean;
    delete_products: boolean;
    batch_size: number;
    skip_products_without_sku: boolean;
    new_product_status?: 'draft' | 'publish';  // Status za nove proizvode (default: draft)
  };
  // Source filters - filteri za source proizvode
  source_filters?: WooShopSourceFilters;
  // Custom field mappings - mapiranje Supabase polja na WooCommerce custom fields
  custom_field_mappings?: CustomFieldMapping[];
  cron_schedule: string;
  cron_enabled: boolean;
  last_sync_at?: string;
  last_sync_status?: string;
  last_sync_stats?: {
    created: number;
    updated: number;
    deleted: number;
    skipped: number;
    errors: number;
  };
  created_at?: string;
  updated_at?: string;
}

// NOTE: All Supabase calls below have been routed through the admin API
// (Firebase-authenticated, service_role server-side). This keeps the
// publishable key from needing woo_shops access in the bundle.
// Imported lazily inside each method to avoid a top-level circular import
// with ../config/firebase in environments that initialize supabase first.
async function _adminApi() {
  const mod = await import('./adminApi');
  return mod.adminApi;
}

export const wooShopService = {
  async getAll(): Promise<WooShop[]> {
    return (await _adminApi()).shops.list();
  },

  async getActive(): Promise<WooShop[]> {
    const all = await (await _adminApi()).shops.list();
    return all.filter((s: WooShop) => s.is_active);
  },

  async getById(id: string): Promise<WooShop | null> {
    try {
      return await (await _adminApi()).shops.get(id);
    } catch (err: any) {
      // Mirror previous behaviour: swallow "not found", rethrow others
      if (String(err?.message || '').includes('404')) return null;
      throw err;
    }
  },

  async create(shop: Omit<WooShop, 'id' | 'created_at' | 'updated_at'>): Promise<WooShop> {
    return (await _adminApi()).shops.create(shop);
  },

  async update(id: string, shop: Partial<WooShop>): Promise<WooShop> {
    return (await _adminApi()).shops.update(id, shop);
  },

  async delete(id: string): Promise<void> {
    await (await _adminApi()).shops.remove(id);
  },

  async toggleActive(id: string, isActive: boolean): Promise<WooShop> {
    return this.update(id, { is_active: isActive });
  },

  async testConnection(shop: WooShop): Promise<{ success: boolean; error?: string }> {
    try {
      const auth = btoa(`${shop.consumer_key}:${shop.consumer_secret}`);
      const response = await fetch(`${shop.url}/wp-json/wc/v3/system_status`, {
        headers: {
          'Authorization': `Basic ${auth}`,
        },
      });

      if (!response.ok) {
        return { success: false, error: `HTTP ${response.status}: ${response.statusText}` };
      }

      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  },

  async triggerSync(shopId?: string): Promise<any> {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    let url = `${supabaseUrl}/functions/v1/sync-to-woo`;
    if (shopId) {
      url += `?shop_id=${shopId}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${supabaseAnonKey}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Sync failed: ${text}`);
    }

    return response.json();
  },

};
